import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { createAxiosInstance } from "api/axiosConfig";

export const useNoteAudio = ({ consultationId, tenantVal, onTranscribed }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const loadAudioForPlayback = useCallback(
    async (noteId) => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}/consultationnote/audio/${noteId}`,
          { responseType: "blob" }
        );
        const audioBlob = new Blob([response.data], { type: "audio/wav" });
        setAudioUrl(URL.createObjectURL(audioBlob));
      } catch (error) {
        console.error("Error loading audio:", error);
      }
    },
    [tenantVal]
  );

  const uploadAudio = useCallback(
    async (audioBlob) => {
      try {
        const formData = new FormData();
        formData.append("file", audioBlob, "consultation-audio.wav");
        formData.append("languageCode", "en-US");

        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${tenantVal}/consultationnote/scribe/${consultationId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (response.data?.note) {
          onTranscribed?.(response.data);
          if (response.data.audioFilePath) {
            loadAudioForPlayback(response.data.id);
          }
          toast.success("Audio transcribed successfully!");
        }
      } catch (error) {
        console.error("Error uploading audio:", error);
        toast.error("Failed to process audio. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    },
    [tenantVal, consultationId, onTranscribed, loadAudioForPlayback]
  );

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        await uploadAudio(audioBlob);
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Failed to start recording. Please check microphone permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);
      toast.success("Recording stopped, processing...");
    }
  };

  const toggleAudioPlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  return {
    isRecording,
    isProcessing,
    isPlaying,
    setIsPlaying,
    audioUrl,
    setAudioUrl,
    audioRef,
    startRecording,
    stopRecording,
    toggleAudioPlayback,
    loadAudioForPlayback,
  };
};
